import { Widget } from '../../core/Widget.js';
import { ItemsCmdController } from '../../core/items/ItemsCmdController.js';
import { ListboxItemsController } from './ListboxItemsController.js';

/**
 * Виджет «список с множественным выбором».
 *
 * ## Назначение
 * Управляет группой `<input type="checkbox">` внутри контейнера и
 * предоставляет контракт получения/установки значений (`WithMainValue`).
 *
 * ## Разметка
 * Контейнер должен иметь класс `im-listbox` и атрибут `data-key`:
 * ```html
 * <div class="im-listbox" data-key="tags">
 *   <label><input type="checkbox" value="a"> A</label>
 *   <label><input type="checkbox" value="b"> B</label>
 *   <label><input type="checkbox" value="c"> C</label>
 * </div>
 * ```
 *
 * ## Значения
 * - Берутся только из `input.value`.
 * - Пустые `value` игнорируются.
 *
 * ## Контракты
 * - `getValue()`:
 *   - возвращает `string[]`, если `splitter` равен `,` (по умолчанию) или не задан;
 *   - возвращает `string` с объединёнными значениями через `splitter`, если он задан и отличается от `,`;
 *   - возвращает `null`, если выбор пуст.
 * - `setValue(vals)`:
 *   - принимает `string[]` или `null/undefined`;
 *   - массив → отметить только эти значения;
 *   - `null/undefined` → снять все.
 * - `hasValue()` → `true`, если выбран хотя бы один элемент.
 *
 * Примечание: Виджет **не генерирует событий** `change` — логика подписки на изменения остаётся на уровне приложения.
 */
class ListboxWidget extends Widget {
    static _cssKey() {
        return "im-listbox";
    }
    /** Получение чекбоксов виджета */
    checkboxes() {
        return this.element.querySelectorAll('input[type="checkbox"]');
    }
    _create(op) {
        this._splitter = op.splitter ?? ",";
        // Инициализация контроллера
        this._itemsCtrl = new ListboxItemsController(this);
        this.items = new ItemsCmdController({
            selection: this._itemsCtrl,
            order: this._itemsCtrl
        });
    }
    /** Проверка наличия выбранных элементов. */
    hasValue() {
        for (const cb of Array.from(this.checkboxes())) {
            if (cb.checked)
                return true;
        }
        return false;
    }
    /**
     * Получение выбранных значений.
     * @returns массив строк или строку с объединёнными значениями, либо null.
     */
    getValue() {
        const values = [];
        this.checkboxes().forEach(cb => {
            if (cb.checked && !cb.disabled && cb.value !== "") {
                values.push(cb.value);
            }
        });
        if (values.length === 0)
            return null;
        return this._splitter === "," ? values : values.join(this._splitter);
    }
    /**
     * Установка набора значений.
     * @param val Массив строк или null/undefined для очистки.
     */
    setValue(val) {
        const set = new Set(Array.isArray(val) ? val.map(String) : []);
        this.checkboxes().forEach(cb => {
            if (cb.disabled)
                return;
            const v = cb.value;
            const shouldCheck = val != null && v !== "" && set.has(v);
            if (cb.checked !== shouldCheck) {
                cb.checked = shouldCheck;
            }
        });
        return true;
    }
    /** Получение названия по значенийю */
    /** Получение названия по значенийю */
    getItemTitle(value) {
        const v = (value ?? "").trim();
        if (!v)
            return null;
        const cb = this.element.querySelector(`input[type="checkbox"][value="${CSS.escape(v)}"]`);
        if (!cb)
            return null;
        const clean = (s) => s.replace(/\s+\(\d+\)$/u, "").trim();
        const id = cb.id;
        if (id) {
            const lbl = this.element.querySelector(`label[for="${CSS.escape(id)}"]`);
            const text = (lbl?.textContent ?? "").trim();
            const t = clean(text);
            return t || v;
        }
        // fallback: label рядом
        const near = cb.nextElementSibling;
        const nearText = (near instanceof HTMLLabelElement ? near.textContent : "")?.trim() ?? "";
        const t2 = clean(nearText);
        return t2 || v;
    }
    _onDestroy() {
    }
}
/** Имя виджета */
ListboxWidget._alias = "Listbox";

export { ListboxWidget };
